<template>
  <div class="card-hover group bg-white rounded-xl shadow-sm overflow-hidden">
    <!-- Image - Clickable -->
    <router-link :to="`/product/${product.id}`" class="block relative aspect-square overflow-hidden">
      <img
        v-if="product.image_url"
        :src="getImageUrl(product.image_url)"
        :alt="product.name"
        class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
      >
      <div
        v-else
        class="w-full h-full flex items-center justify-center bg-gray-100"
      >
        <PhotoIcon class="h-16 w-16 text-gray-300" />
      </div>
      
      <!-- Badges -->
      <div class="absolute top-2 left-2 flex flex-col gap-1">
        <span
          v-if="product.promotional_price"
          class="badge-red"
        >
          Promoção
        </span>
        <span
          v-if="product.is_featured"
          class="badge-yellow"
        >
          Destaque
        </span>
      </div>
      
      <!-- View Details Overlay -->
      <div class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <span class="text-white font-medium px-4 py-2 border-2 border-white rounded-lg">
          Ver Detalhes
        </span>
      </div>
    </router-link>
    
    <!-- Content -->
    <div class="p-4">
      <!-- Product Name - Clickable -->
      <router-link :to="`/product/${product.id}`">
        <h3 class="font-semibold text-gray-900 mb-1 line-clamp-2 hover:text-orange-600 transition-colors">
          {{ product.name }}
        </h3>
      </router-link>
      
      <p class="text-sm text-gray-500 mb-3 line-clamp-2">
        {{ product.description || 'Sem descrição' }}
      </p>
      
      <!-- Price -->
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-baseline gap-2">
          <span class="text-lg font-bold text-orange-600">
            {{ formatCurrency(product.current_price || product.price) }}
          </span>
          <span
            v-if="product.promotional_price"
            class="text-sm text-gray-400 line-through"
          >
            {{ formatCurrency(product.price) }}
          </span>
        </div>
      </div>
      
      <!-- Quantity Selector and Add to Cart -->
      <div class="flex items-center gap-3">
        <!-- Quantity -->
        <div class="flex items-center border rounded-lg">
          <button
            @click="quantity > 1 && quantity--"
            class="p-2 hover:bg-gray-100"
            :disabled="quantity <= 1"
          >
            <MinusIcon class="h-4 w-4" />
          </button>
          <span class="px-3 font-medium w-10 text-center">{{ quantity }}</span>
          <button
            @click="quantity++"
            class="p-2 hover:bg-gray-100"
          >
            <PlusIcon class="h-4 w-4" />
          </button>
        </div>
        
        <!-- Add to Cart -->
        <button
          @click="addToCart"
          class="flex-1 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors bg-orange-500 hover:bg-orange-600"
          :disabled="!product.is_available"
          :class="{ 'opacity-50 cursor-not-allowed': !product.is_available }"
        >
          <span v-if="product.is_available">Adicionar</span>
          <span v-else>Indisponível</span>
        </button>
      </div>
    </div>
    
    <!-- Modal de produto adicionado -->
    <AddedToCartModal
      :show="showAddedModal"
      :product-name="product.name"
      :quantity="addedQuantity"
      @close="showAddedModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { PhotoIcon, MinusIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { useCartStore } from '@/stores'
import { formatCurrency } from '@/utils/formatters'
import AddedToCartModal from '@/components/cart/AddedToCartModal.vue'
import type { Product } from '@/types'

const props = defineProps<{
  product: Product
}>()

const cartStore = useCartStore()
const quantity = ref(1)
const showAddedModal = ref(false)
const addedQuantity = ref(1)

const getImageUrl = (url: string) => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return url
}

const addToCart = () => {
  if (!props.product.is_available) return
  
  cartStore.addItem(props.product, quantity.value)
  addedQuantity.value = quantity.value
  showAddedModal.value = true
  quantity.value = 1 // Reset quantity after adding
}
</script>
